/**
 * Catálogo visual único dos nodes do construtor de fluxos.
 *
 * O construtor e o Analytics devem consumir este mesmo mapa para impedir que
 * um novo tipo seja exibido pelo React Flow com o cartão genérico de fallback.
 *
 * RELEASE: 2026-10-02 v1.0.0 - Centraliza os 48 tipos visuais de node e inclui
 *          o node Conexão no canvas do Analytics.
 */

import type { ComponentType } from 'react';
import type { NodeTypes } from '@xyflow/react';
import type { NodeType } from '@/types/flows';
import { StartNode } from './StartNode';
import { MessageNode } from './MessageNode';
import { TemplateNode } from './TemplateNode';
import { QuestionNode } from './QuestionNode';
import { ConditionNode } from './ConditionNode';
import { ActionNode } from './ActionNode';
import { AssignPhaseNode } from './AssignPhaseNode';
import { DelayNode } from './DelayNode';
import { InputNode } from './InputNode';
import { SetVariableNode } from './SetVariableNode';
import { FilterArrayNode } from './FilterArrayNode';
import { FormatArrayNode } from './FormatArrayNode';
import { EndNode } from './EndNode';
import { EndFlowNode } from './EndFlowNode';
import { WebhookNode } from './WebhookNode';
import { GotoNode } from './GotoNode';
import { ABTestNode } from './ABTestNode';
import { TriggerCheckNode } from './TriggerCheckNode';
import { PauseBotNode } from './PauseBotNode';
import { ResumeBotNode } from './ResumeBotNode';
import { CrmCreateDealNode } from './CrmCreateDealNode';
import { CrmChangePhaseNode } from './CrmChangePhaseNode';
import { CrmAssignSellerNode } from './CrmAssignSellerNode';
import { CrmCreateActivityNode } from './CrmCreateActivityNode';
import { CrmSchedulingLinkNode } from './CrmSchedulingLinkNode';
import { BusinessHoursNode } from './BusinessHoursNode';
import { MessageTypeCheckNode } from './MessageTypeCheckNode';
import { InstagramMessageNode } from './InstagramMessageNode';
import { CarouselNode } from './CarouselNode';
import { GroupNode } from './GroupNode';
import { TrackingNode } from './TrackingNode';
import { EmbedNode } from './EmbedNode';
import { NotificationNode } from './NotificationNode';
import { ContractGenerateNode } from './ContractGenerateNode';
import { ConnectionNode } from './ConnectionNode';
import { ChargeGenerateNode } from './ChargeGenerateNode';
import { TaskCreateNode } from './TaskCreateNode';
import { TaskChangePhaseNode } from './TaskChangePhaseNode';
import { TaskAssignNode } from './TaskAssignNode';

/**
 * O `satisfies` torna a cobertura exaustiva: adicionar um `NodeType` exige
 * registrar aqui o respectivo componente antes de o projeto compilar.
 */
export const FLOW_NODE_COMPONENTS = {
  start: StartNode,
  message_text: MessageNode,
  message_media: MessageNode,
  message_template: TemplateNode,
  message_carousel: CarouselNode,
  question_buttons: QuestionNode,
  question_list: QuestionNode,
  input_data: InputNode,
  condition: ConditionNode,
  delay: DelayNode,
  add_tag: ActionNode,
  remove_tag: ActionNode,
  assign_phase: AssignPhaseNode,
  update_contact: ActionNode,
  transfer_human: ActionNode,
  transfer_department: ActionNode,
  set_variable: SetVariableNode,
  filter_array: FilterArrayNode,
  format_array: FormatArrayNode,
  webhook: WebhookNode,
  goto: GotoNode,
  ab_test: ABTestNode,
  trigger_check: TriggerCheckNode,
  pause_bot: PauseBotNode,
  resume_bot: ResumeBotNode,
  crm_create_deal: CrmCreateDealNode,
  crm_change_phase: CrmChangePhaseNode,
  crm_assign_seller: CrmAssignSellerNode,
  crm_create_activity: CrmCreateActivityNode,
  crm_scheduling_link: CrmSchedulingLinkNode,
  business_hours: BusinessHoursNode,
  message_type_check: MessageTypeCheckNode,
  instagram_message: InstagramMessageNode,
  send_button_link: ActionNode,
  group: GroupNode,
  google_tag_manager: TrackingNode,
  meta_pixel: TrackingNode,
  google_analytics: TrackingNode,
  embed: EmbedNode,
  send_notification: NotificationNode,
  contract_generate: ContractGenerateNode,
  charge_generate: ChargeGenerateNode,
  connection_query: ConnectionNode,
  task_create: TaskCreateNode,
  task_change_phase: TaskChangePhaseNode,
  task_assign: TaskAssignNode,
  end: EndNode,
  end_flow: EndFlowNode,
} satisfies Record<NodeType, ComponentType<any>>;

/** Mapa estável consumido diretamente pelo canvas editável. */
export const FLOW_NODE_TYPES: NodeTypes = FLOW_NODE_COMPONENTS;